/**
 * Button component — shared types and Zod prop schema.
 *
 * Variant properties follow the Figma "UI Design System" component set:
 * Size × Type × State × Icon × Text
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';
import { actionIdSchema } from '@galapagos/product-analytics';
import type { SpinnerTone } from '../Spinner/Spinner';
import { iconNames } from '../../elements/icons/registry';

export const ButtonSize = z.enum(['L', 'M', 'M/L', 'S']);
export type ButtonSize = z.infer<typeof ButtonSize>;

export const ButtonType = z.enum(['Primary', 'Brand', 'Secondary', 'Critical']);
export type ButtonType = z.infer<typeof ButtonType>;

export const ButtonState = z.enum(['Default', 'Loading', 'Disabled', 'Inactive', 'Selected']);
export type ButtonState = z.infer<typeof ButtonState>;

/** Where the icon sits relative to the label, for icon+text buttons. `leading`
 * (default) preserves every existing consumer's rendering; `trailing` places
 * the icon after the label (e.g. a dropdown control's trailing chevron). */
export const ButtonIconPosition = z.enum(['leading', 'trailing']);
export type ButtonIconPosition = z.infer<typeof ButtonIconPosition>;
export const BUTTON_DEFAULT_ICON_POSITION: ButtonIconPosition = 'leading';

/**
 * Horizontal sizing within the parent. `fill` preserves the large CTA
 * behavior; `hug` lets a large pill remain intrinsic when composed inline.
 */
export const ButtonWidth = z.enum(['fill', 'hug']);
export type ButtonWidth = z.infer<typeof ButtonWidth>;

/**
 * Web-only width values. `stretch` fills the parent with no Large-button cap,
 * for a web layout whose column is wider than the cap. It stays out of
 * `buttonPropsSchema`, so a catalog document can never carry it and native
 * never sees it.
 */
export const WebButtonWidth = z.enum([...ButtonWidth.options, 'stretch']);
export type WebButtonWidth = z.infer<typeof WebButtonWidth>;

/** Base shape — plain ZodObject so `formatZodType` can introspect props. */
const buttonBaseSchema = z
  .object({
    size: ButtonSize,
    type: ButtonType,
    state: ButtonState.default('Default'),
    /** Muted background with a readable label; the action remains available for feedback. */
    muted: z.boolean().optional(),
    icon: z.boolean().default(false),
    text: z.boolean().default(true),
    label: z.string().optional(),
    iconName: z.enum(iconNames).optional(),
    iconPosition: ButtonIconPosition.optional(),
    width: ButtonWidth.optional(),
    // An icon-glyph-size override for a caller whose icon must read smaller
    // than the button's own `size` scale (e.g. a small trailing chevron on an
    // otherwise normal-height dropdown control) — mirrors the existing
    // `width` override pattern (Label, Stack): overrides one dimension for a
    // caller with an exact external constraint, default omitted for every
    // other consumer.
    iconSize: z.number().positive().optional(),
    accessibilityLabel: z.string().optional(),
    accessibilityHint: z.string().optional(),
    testID: z.string().optional(),
    actionId: actionIdSchema.optional(),
    /** Bounded discriminator from a closed set for buttons that share one
     * `actionId` across sibling instances. Travels as the `element_key` action
     * property; never free text. */
    elementKey: z.string().optional(),
  })
  .strict();

/** Catalog-facing schema (plain ZodObject). */
export const buttonPropsSchema = buttonBaseSchema;

/** Runtime schema with cross-field refinements. */
export const buttonPropsRefinedSchema = buttonBaseSchema
  .refine((data) => !data.text || (data.label && data.label.length > 0), {
    message: 'label is required when text is true',
    path: ['label'],
  })
  .refine((data) => !data.icon || (data.iconName && data.iconName.length > 0), {
    message: 'iconName is required when icon is true',
    path: ['iconName'],
  });

export type ButtonProps = z.input<typeof buttonPropsSchema>;

/** Dimensions for icon-only buttons (Icon=True, Text=False), matching Figma spec. */
export const iconOnlyDimensions: Record<ButtonSize, { width: number; height: number }> = {
  L: { width: 80, height: 80 },
  'M/L': { width: 68, height: 68 },
  M: { width: 44, height: 44 },
  S: { width: 24, height: 24 },
};

/** Heights for text buttons, matching Figma spec. */
export const textButtonHeight: Record<ButtonSize, number> = {
  L: 68,
  'M/L': 68,
  M: 44,
  S: 26,
};

/**
 * Touch-area expansion, in dp on every side, that the native host gives a
 * `size: 'M'` button: M paints 44 dp, under the 48 dp Android minimum
 * (ADR-025 Rule 5). A hit area is clipped to its parent, so the parent must
 * leave this much room around the button for the expansion to count.
 */
export const M_BUTTON_HIT_SLOP = (sizing.touchTarget.minimum - textButtonHeight.M) / 2;

interface HitSlopDocumentElement {
  type: string;
  props?: Record<string, unknown>;
  children?: readonly string[];
}

/**
 * The Stacks in a document that hold two or more `size: 'M'` buttons as direct
 * children with a gap narrower than both buttons' slop together, so their
 * expanded touch areas would overlap and a tap meant for one could land on the
 * other. Siblings are what the check can see: each button's slop is clipped to
 * its own parent.
 */
export function crowdedMButtonStacks(
  elements: Readonly<Record<string, HitSlopDocumentElement>>,
): string[] {
  const crowded: string[] = [];
  for (const [id, element] of Object.entries(elements)) {
    if (element.type !== 'Stack') continue;
    const mButtons = (element.children ?? []).filter((childId) => {
      const child = elements[childId];
      return child?.type === 'Button' && child.props?.size === 'M';
    });
    if (mButtons.length < 2) continue;
    // An absent or `none` gap is no spacing at all.
    const gapKey = element.props?.gap;
    const gap = Object.entries(spacing).find(([key]) => key === gapKey)?.[1] ?? 0;
    if (gap < 2 * M_BUTTON_HIT_SLOP) crowded.push(id);
  }
  return crowded;
}

/**
 * Max width for the Large (primary CTA) text button. It fills its container
 * (width 100%) up to this cap, so it reads as ~370px on roomy screens and
 * shrinks to fit on narrower ones. M and S hug their content instead.
 */
export const LARGE_BUTTON_MAX_WIDTH = 370;

/**
 * Horizontal padding per size, matching Figma spec. Text-only and icon+text
 * buttons differ at size M (24px text-only vs 16px with an icon). Icon-only
 * buttons ignore these — they use the fixed squares in `iconOnlyDimensions`.
 */
export const horizontalPaddingForSize: Record<ButtonSize, { textOnly: number; iconText: number }> =
  {
    L: { textOnly: spacing.lg, iconText: spacing.lg }, // 24 / 24
    'M/L': { textOnly: spacing.lg, iconText: spacing.lg }, // 24 / 24
    M: { textOnly: spacing.lg, iconText: spacing.m }, // 24 / 16
    S: { textOnly: spacing.sm, iconText: spacing.sm }, // 12 / 12
  };

/** Gap between icon and text per size, matching Figma spec. */
export const iconTextGapForSize: Record<ButtonSize, number> = {
  L: spacing.m, // 16
  'M/L': spacing.m, // 16
  M: spacing.s, // 8
  S: spacing.s, // 8
};

/** Icon glyph size per button size, matching Figma spec. */
export const iconSizeForSize: Record<ButtonSize, number> = {
  L: sizing.icon.sm, // 24
  'M/L': sizing.icon.md, // 32
  M: sizing.icon.sm, // 24
  S: sizing.icon.xs, // 16
};

/** Background colour for each Type × State combination. Single source of truth. */
export const backgroundForVariant: Record<ButtonType, Record<ButtonState, string>> = {
  Primary: {
    Default: colors.button.primary,
    Loading: colors.button.primary,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.primaryPressed,
  },
  Brand: {
    Default: colors.button.brand,
    Loading: colors.button.brand,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.brandPressed,
  },
  Secondary: {
    Default: colors.button.secondary,
    Loading: colors.button.inactive,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.background.tertiary,
  },
  Critical: {
    Default: colors.button.critical,
    Loading: colors.button.inactive,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.critical,
  },
};

/** Pressed background colour for each Type × State combination. Single source of truth. */
export const pressedBackgroundForVariant: Record<ButtonType, Record<ButtonState, string>> = {
  Primary: {
    Default: colors.button.primaryPressed,
    Loading: colors.button.primary,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.primaryPressed,
  },
  Brand: {
    Default: colors.button.brandPressed,
    Loading: colors.button.brand,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.brandPressed,
  },
  Secondary: {
    Default: colors.button.secondary,
    Loading: colors.button.inactive,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.background.tertiary,
  },
  Critical: {
    Default: colors.button.critical,
    Loading: colors.button.inactive,
    Disabled: colors.button.disabled,
    Inactive: colors.button.inactive,
    Selected: colors.button.critical,
  },
};

/** Text colour for each Type × State combination. Single source of truth. */
export const textColorForVariant: Record<ButtonType, Record<ButtonState, string>> = {
  Primary: {
    Default: colors.text.inverted,
    Loading: colors.text.inverted,
    Disabled: colors.text.tertiary,
    Inactive: colors.text.inverted,
    Selected: colors.text.inverted,
  },
  Brand: {
    Default: colors.text.inverted,
    Loading: colors.text.inverted,
    Disabled: colors.text.tertiary,
    Inactive: colors.text.inverted,
    Selected: colors.text.inverted,
  },
  Secondary: {
    Default: colors.text.primary,
    Loading: colors.text.inverted,
    Disabled: colors.text.tertiary,
    Inactive: colors.text.inverted,
    Selected: colors.text.primary,
  },
  Critical: {
    Default: colors.text.inverted,
    Loading: colors.text.inverted,
    Disabled: colors.text.tertiary,
    Inactive: colors.text.inverted,
    Selected: colors.text.inverted,
  },
};

/** Typography style for each Size. Single source of truth. */
export const typographyForSize: Record<ButtonSize, (typeof typography)[keyof typeof typography]> = {
  L: typography.button1,
  'M/L': typography.button1,
  M: typography.button2,
  S: typography.button3,
};

/** Shared Spinner diameter per button size, matching the icon glyph sizing. */
export const buttonSpinnerSizeForSize: Record<ButtonSize, number> = {
  L: sizing.icon.sm, // 24
  'M/L': sizing.icon.sm, // 24
  M: sizing.icon.xs, // 16
  S: sizing.icon.xxs, // 12
};

/**
 * Shared Spinner tone per button type. The spinner only renders in the Loading
 * state, and every variant's Loading background is dark (Primary uses
 * `button.primary`, Secondary & Critical use `button.inactive`), so the light
 * `inverted` arc stays visible for all three — matching the pre-existing white
 * (`text.inverted`) loading foreground.
 */
export const buttonSpinnerToneForType: Record<ButtonType, SpinnerTone> = {
  Primary: 'inverted',
  Brand: 'inverted',
  Critical: 'inverted',
  Secondary: 'inverted',
};
